import React, { useState, useEffect } from 'react';
import { Box, Button } from 'grommet';
import { Youtube, Next } from 'grommet-icons';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDText from '@ui/Text/SHDText';
import HowToAndErrorModal from '@helpers/HowToAndErrorStreamingModal';
import { LiveStreamSettingsModal } from '@helpers/LiveStreamSettingsModal';
import SHDButton from '@ui/Button/SHDButton';
import { analytics } from '@services/analytics';
import {
  YOUTUBE_HOW_TO_SET_UP_STREAMING,
  YOUTUBE_STREAMING_ACCESS_ERROR,
  YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_ACCESS_ERROR_TITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE,
} from '@constants/streamingHelpTexts';
import useYoutubeConnection from '@helpers/useYoutubeConnection';

interface Props {
  teamId: string;
  isAdmin: boolean;
  truncateLength?: number | null;
  sportType: string;
}

const YouTubeButton: React.FC<Props> = ({
  teamId,
  truncateLength = null,
  sportType,
}) => {
  const { signIn, cancelSignin, status } = useYoutubeConnection({ teamId });
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showHowToSetUpModal, setShowHowToSetUpModal] = useState(false);
  const [showErrorLayer, setShowErrorLayer] = useState(false);

  const formatTeamName = (name: string) =>
    truncateLength ? name.slice(0, truncateLength) + '...' : name;

  useEffect(() => {
    if (status?.phase === 'error') {
      setShowErrorLayer(true);
    }
    if (status?.phase === 'completed') {
      analytics.track('Stream Destination Linked', {
        teamId: teamId,
        sport: sportType,
        destinationType: 'YouTube',
        destinationName: status?.channel?.credsChannelName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleClick = () => {
    if (status?.phase === 'in-progress') {
      cancelSignin();
    } else {
      setShowIntroModal(true);
    }
  };

  return (
    <Box>
      <SHDCallout round="medium">
        <Button
          onClick={
            status?.channel
              ? () =>
                  window.open(
                    `https://www.youtube.com/channel/${status?.channel?.credsChannelId}`,
                    '_blank'
                  )
              : handleClick
          }
          style={{ width: '100%' }}
        >
          <Box direction="row" align="center" justify="between" pad="xsmall">
            <Box
              width="48px"
              height="48px"
              round="full"
              align="center"
              justify="center"
              style={{
                backgroundColor: status?.channel ? '#FF0000' : '#B6B6B6',
              }}
            >
              <Youtube
                size="24px"
                color={status?.channel ? 'white' : '#FFFFFF'}
              />
            </Box>
            <Box flex style={{ marginLeft: '16px' }}>
              <SHDText style="subhead-semibold">YouTube</SHDText>
              <SHDText
                style="footnote"
                color={status?.channel ? '#7C7C7C' : '#9E9E9E'}
              >
                {status?.channel
                  ? formatTeamName(status.channel.credsChannelName)
                  : 'Link account'}
              </SHDText>
            </Box>
            <Box flex={{ shrink: 0, grow: 0 }}>
              <Next
                size="14px"
                color={status?.channel ? 'black' : 'black-alpha-30'}
              />
            </Box>
          </Box>
        </Button>
      </SHDCallout>

      {showIntroModal && (
        <LiveStreamSettingsModal
          headerText="LINK YOUTUBE"
          title="Have you ever livestreamed on YouTube?"
          onCloseModal={() => setShowIntroModal(false)}
        >
          <SHDButton
            secondary
            label={
              <SHDText size="button" style="subhead-bold" textAlign="center">
                Yes, I have YouTube Studio set up
              </SHDText>
            }
            onClick={() => {
              analytics.track('Destination Path Choice Selected', {
                pathSelected: 'Yes',
                destinationType: 'YouTube',
                teamId,
              });
              setShowIntroModal(false);
              signIn();
            }}
          />
          <SHDButton
            secondary
            label={
              <SHDText size="button" style="subhead-bold" textAlign="center">
                No, I need help setting up
              </SHDText>
            }
            onClick={() => {
              setShowHowToSetUpModal(true);
              analytics.track('Destination Path Choice Selected', {
                pathSelected: 'No',
                destinationType: 'YouTube',
                teamId,
              });
            }}
          />
        </LiveStreamSettingsModal>
      )}

      {showHowToSetUpModal && (
        <HowToAndErrorModal
          headerText="LINK YOUTUBE"
          title="How to set up YouTube livestreaming"
          subtitle=""
          onClose={() => setShowHowToSetUpModal(false)}
          isError={false}
          htmlContent={YOUTUBE_HOW_TO_SET_UP_STREAMING}
        />
      )}

      {showErrorLayer && status?.error && (
        <HowToAndErrorModal
          headerText="LINK YOUTUBE"
          title={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_TITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE
          }
          subtitle={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE
          }
          onClose={() => setShowErrorLayer(false)}
          isError={true}
          htmlContent={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR
          }
        />
      )}
    </Box>
  );
};

export default YouTubeButton;
